import {useCallback, useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import cap17drive from "../index";
import {clearSelectedEpisode} from "../actions";
import useViewTMDBContent from "./useViewTMDBContent";
import {fetchGetTorresowo} from "../../../utils/utils";
import auth from "../../../utils/authentication";
import {SquadflixSocketEmitEvents} from "../../app/squadflix-tv-mode/socket-utils/socket-send-events";

export const useEpisodeDetailsData = (
    serieId: string,
    seasonNumber: string | number,
    episodeNumber: string | number,
    squadflixTvHook?: any
) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const seasonSelector = useSelector(cap17drive.selectors.getSelectedSeason);
    const serieSelector = useSelector(cap17drive.selectors.getSelectedSerie);

    const [nextEpisode, setNextEpisode] = useState<any>(null);
    const {thing: episode, markThingAsView} = useViewTMDBContent();
    const [playing, setPlaying] = useState(false);
    const [season, setSeason] = useState<any>(seasonSelector);
    const [serie, setSerie] = useState<any>(serieSelector);

    useEffect(() => {
        if (seasonSelector) {
            setSeason(seasonSelector);
        }
    }, [seasonSelector, seasonNumber]);

    useEffect(() => {
        if (serieSelector) {
            setSerie(serieSelector);
        }
    }, [serieSelector]);

    const getNextEpisode = useCallback(async (ep: any) => {
        if (!ep || !auth.currentUser) return;
        const next = await fetchGetTorresowo(
            `/tmdb/serie/${serieId}/season/${ep.season_number}/episode/${ep.episode_number + 1}?uid=${auth.currentUser.uid}`
        ).then(res => {
            if (res?.status_code) return null;
            res.show_id = serieId;
            return res;
        }).catch(() => null);

        if (!next) {
            const nextSeasonNumber = ep.season_number + 1;
            fetchGetTorresowo(
                `/tmdb/serie/${serieId}/season/${nextSeasonNumber}/episode/1?uid=${auth.currentUser.uid}`
            ).then(res => {
                if (res?.status_code) return;
                res.show_id = serieId;
                setNextEpisode(res);
            }).catch(() => {});
        } else {
            setNextEpisode(next);
        }
    }, [serieId]);

    useEffect(() => {
        if (episode) {
            setNextEpisode(null);
            getNextEpisode(episode);
        }
    }, [episode, getNextEpisode]);

    const episodeItem = useMemo(() => {
        if (!episode || !serie) return null;
        return {
            ...episode,
            nameEN: serie?.nameEN,
            original_name: serie?.original_name,
            serie: serie,
            nextEpisode: nextEpisode
        };
    }, [episode, serie, nextEpisode]);

    useEffect(() => {
        if (!squadflixTvHook?.connectedReceptor) return;
        SquadflixSocketEmitEvents.sendViewingItem(squadflixTvHook.connectedReceptor, episodeItem, "tv" as any);
        return () => {
            SquadflixSocketEmitEvents.sendStopViewingItem(squadflixTvHook.connectedReceptor);
        };
    }, [episodeItem, squadflixTvHook?.connectedReceptor]);

    useEffect(() => {
        if (!auth.currentUser) return;
        if (!serie || serie.show_id != serieId) {
            dispatch(cap17drive.actions.clearSelectedSerie() as any);
            dispatch(cap17drive.actions.clearSelectedSeason() as any);
            dispatch(cap17drive.actions.fetchSerie(serieId, auth.currentUser.uid) as any);
        }
        setNextEpisode(null);
        dispatch(cap17drive.actions.clearSelectedEpisode() as any);
        dispatch(cap17drive.actions.fetchEpisode(serieId, seasonNumber, episodeNumber, auth.currentUser.uid) as any);
    }, [dispatch, episodeNumber, seasonNumber, serieId]);

    useEffect(() => {
        if (episode && (!season || season?.season_number != episode.season_number) && auth.currentUser) {
            dispatch(cap17drive.actions.fetchSeason(serieId, episode.season_number, auth.currentUser.uid) as any);
        }
        setPlaying(false);
    }, [dispatch, episode, season, serieId]);

    const goToNextEpisode = useCallback(() => {
        if (!nextEpisode) return;
        const targetSeason = nextEpisode.season_number;
        const targetEpisode = nextEpisode.episode_number;
        setNextEpisode(null);
        markThingAsView(true, () => {
            dispatch(clearSelectedEpisode() as any);
            navigate(`/squadflix/series/${serieId}/${targetSeason}/${targetEpisode}`);
        });
    }, [dispatch, markThingAsView, navigate, nextEpisode, serieId]);

    return {
        serie,
        season,
        episode,
        episodeItem,
        nextEpisode,
        playing,
        setPlaying,
        markThingAsView,
        goToNextEpisode,
        loading: !episode || !season || !serie,
    };
};

export default useEpisodeDetailsData;
